import { createAsyncThunk, createDraftSafeSelector, createSlice } from '@reduxjs/toolkit';
import http from '../utils/http';
import { Address, County } from './AddressSlice';
import { Afr } from './AfrSlice';
import { Reference } from './TaxonomyReferenceSlice';
import { LogicalPeriodEnum, TaxonomyTypeEnum } from './TaxonomyVersionSlice';
import { logerUser } from './UserSlice';
import { RootState } from './store';
import dateHelper from '../utils/dateHelper';

export enum AffiliationStatus {
    INDEPENDENT,
    DEPENDENT
}
export interface EditEntityQuery {
    entityId: string,
    taxonomyYear: number
}
export interface EntityReference {
    code: string;
    name: string;
    id: string;
    category: string;
}
export interface AfrStatus {
    afrYear: number;
    type: TaxonomyTypeEnum | null;
    logicalPeriod: LogicalPeriodEnum | null;
    status: string;
}
export interface Affiliation {
    affiliate: EntityReference;
    affiliateId: string;
    effectiveDate: string | null;
    endDate: string | null;
    prior: Affiliation | null;
}

export interface AffiliationInput {
    affiliate: EntityReference;
    affiliateId: string;
    effectiveDate: string | null;
    endDate: string | null;
}

export interface EntityContactUser {
    lastName: string;
    firstName: string;
    role: string;
}

export interface EntityAuthorization {
    endDate: string | null;
    entityContactRole: string;
    entityUserRole: string;
    reportAccess: string;
    startDate: string;
}

export interface EntityContact {
    contact: EntityContactUser;
    title: string;
    entityAuthorizations: Array<EntityAuthorization>
}

export interface EntityReportAccessChangeInput {
    state: string;
    value: boolean;
    timestamp: string;
}

export interface EntityStateChangeEvent {
    state: string;
    value: string;
    timestamp: string;
    creator: logerUser;
    reason: string | null;
}

export interface EntityReportAccessChange {
    timestamp: string;
    state: string;
    value: string;
}

export interface DoingBusinessAs {
    previousEntity: string | null;
    currentEntity: string | null;
}

export interface EntityInput {
    entityId: string;
    fiscalEndMonth: number | null;
    fiscalEndDay: number | null;
    dissolvedDate: string | null;
    inactiveDate: string | null;
    renamedDate: string | null;
    mergedDate: string | null;
    status: string | null;
    statusReason: string | null;
    address: {
        street: string | null;
        pobox: string | null;
        cityId: string | null;
        zip: string | null;
    }
    name: string;
    nameChangeDate: string | null;
    districttype: string | null;
    createDate: string | null;
    affiliation: AffiliationInput | null;
    typecode: string | null;
    taxonomyYear: number | null;
    entityReportAccessChangeInput: Array<EntityReportAccessChangeInput> | null;
    doingBusinessAs: DoingBusinessAs | null;
    districtDependency: string | null;
    districtDependencyChangeDate: string | null;
    servesInCounties: Array<number> | null;
    //TODO add the others
}

export interface Entity {
    code: string;
    name: string;
    typecode: string;
    districttype: string;
    districtDependency: string;
    afrs: Array<Afr>;
    address: Address;
    affiliation: Affiliation;
    creationDate: string;
    dissolvedDate: string;
    inactiveDate: string;
    renamedDate: string;
    mergedDate: string;
    status: string;
    statusReason: string | null;
    contacts: Array<EntityContact>;
    fiscalEndDay: number;
    fiscalEndMonth: number;
    entityReportAccessChanges: Array<EntityReportAccessChange> | null;
    references: Array<Reference>;
    doingBusinessAs: DoingBusinessAs | null;
    servesInCounties: Array<County> | null;
    mergedEntities: Array<DoingBusinessAs>;
    entityStatusWithReason: string | null;
    isParentEntityActive: boolean | null;
}

export interface EntityState {
    entity: Entity | null;   
    status: 'idle' | 'loading' | 'failed' | 'loadEntityData';
}

const initialState: EntityState = {
    entity: null,
    status: "idle"
}
export const isEntityCode = function (cid: string): boolean {
    if (cid && cid.length === 6) {
        // This is a candidate entity id
        return !isNaN(parseInt(cid));
    }
    return false;
}

export const canReportForType = function(entity: Entity, tType: TaxonomyTypeEnum | null) : boolean {
    if (!tType) {
        return false;
    }
    if (!entity) {
        return false;
    }
    var reportAccess: boolean = false;
    var stateKey = tType + "_ENABLED";
    if (entity.entityReportAccessChanges) {
        entity.entityReportAccessChanges.map(x => {
            if (x.state === stateKey) {
                reportAccess = (x.value === "TRUE");
            }
        });
    }
    return reportAccess;
}
const getNameForReference = function (r: Reference, year: number): string {
    var dtMatch: string = year + "-09-30";
    var d: string = "";
    var n: string = "";
    var nCurrent: string = "";
    r.parts.map((x: any) => {
        switch (x.definition.name) {
            case "PreviousEntityName":
                n = x.value;
                break;

            case "NewEntityName":
                nCurrent = x.value;
                break;

            case "NameChangeDate":
                d = x.value;
                break;
        }
    });
    if (dtMatch < dateHelper.stringToDate(d).toISOString()) {
        return n;
    }
    return nCurrent;
}
export const createNameChangeEntry = function (r: Reference): string {
    var d: string = "";
    var n: string = "";
    r.parts.map((x: any) => {
        switch (x.definition.name) {
            case "PreviousEntityName":
                n = x.value;
                break;

            case "NameChangeDate":
                d = x.value;
                break;
        }
    });
    return " was '" + n + "' before " + d;
}

export const getNameForYear = function (entity: Entity | null, year: number): string {
    if (!entity) {
        return "";
    }
    var result = entity.name;
    if (entity.references) {
        entity.references.map(x => (x.type.indexOf("nameChange") > 0) ? result = getNameForReference(x,year) : "");
    }

    return result;
}
export const getNameForAfr = function (entity: Entity | null, afr : Afr | null): string {
    if (!entity) {
        return "";
    }
    var result = entity.name;
    if (!afr) {
        return result;
    }
    if (entity.references) {
        entity.references.map(x => (x.type.indexOf("nameChange") > 0) ? result = getNameForReference(x, afr.year) : "");
    }
    if (afr.owner && afr.owner.code !== entity.code) {
        result = afr.owner.name;
    }

    return result;
}
export const hasNameChange = function (entity: Entity | null): boolean {
    if (!entity || !entity.references) {
        return false;
    }
    var result = false;
    if (entity.references) {
        entity.references.map(x => (x.type.indexOf("nameChange") > 0) ? result = true : "");
    }
    return result;
}


export const loadEntity = createAsyncThunk(
    "Entity/loadEntity",
    async (query: EditEntityQuery): Promise<Entity | undefined> => {
        var entityId: string = query.entityId;
        var year = query.taxonomyYear;
        const result = await http<undefined, Entity>({
            path: `/entity/${entityId}/${year}`,
        });

        return result.parsedBody;

    });

export const getEntityHistory = createAsyncThunk(
    "Entity/GetEntityHistory",
    async (entityId: string): Promise<Array<EntityStateChangeEvent> | undefined> => {
               
        const result = await http<string, Array<EntityStateChangeEvent>>({
            path: `/entity/getEntityHistory/${entityId}/`,
        });

        return result.parsedBody;

    });

export const loadEditEntity = createAsyncThunk(
    "Entity/loadEditEntity",
    async (query: EditEntityQuery): Promise<Entity | undefined> => {
        var eid = query.entityId;
        var year = query.taxonomyYear;
        const result = await http<undefined, Entity>({
            path: `/entity/editEntity/${eid}/${year}`,
        });

        return result.parsedBody;

    });

export const saveEntityDetail = createAsyncThunk(
    "Entity/saveEntityDetail",
    async (entity: EntityInput): Promise<Entity | undefined> => {

        const result = await http<EntityInput, Entity>({
            path: `/entity/saveEntityAsync`,
            method: `post`,
            body: entity,
        });

        return result.parsedBody;
    });

export const createEntity = createAsyncThunk(
    "Entity/createEntity",
    async (entity: EntityInput): Promise<Entity | undefined> => {

        const result = await http<EntityInput, Entity>({
            path: `/entity/createEntityAsync`,
            method: `post`,
            body: entity,
        });

        return result.parsedBody;
    })

export const getContactList = function (entity: Entity | null) {
    // get all the contacts associated to an entity
    var list: Array<EntityContact> = [];

    if (entity && entity.contacts) {

        // pull the contact information
        for (var contact of entity.contacts) {
            list.push({ contact: contact.contact, title: contact.title, entityAuthorizations: contact.entityAuthorizations });
        }

    }

    return list;

}

export const saveEntityReportAccesses = createAsyncThunk(
    "Entity/SaveEntityReportAccesses",
    async (entityReportAccesses: { item1: string; item2: Array<EntityReportAccessChangeInput> }): Promise<Array<EntityReportAccessChange> | null> => {
        try {
            const result = await http<{ item1: string; item2: Array<EntityReportAccessChangeInput> }, Array<EntityReportAccessChange>>({
                path: `/entity/saveEntityReportAccesses`,
                method: 'post',
                body: entityReportAccesses
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const entitySlice = createSlice({
    name: 'Entity',
    initialState,
    reducers: {
        resetEntityState: (state: any, action: any) => {
            return initialState
        },
        updateAddress(state: any, action: any) {
            state.entity.address = action.payload;
        },
        updateAfrStatus(state: any, action: any) {
            var updateStatus: AfrStatus = action.payload;
            if (state.entity) {
                var e: Entity = state.entity;
                if (e.afrs) {
                    for (var afr of e.afrs) {
                        if (afr.year === updateStatus.afrYear &&
                            afr.taxonomyType === updateStatus.type &&
                            afr.logicalPeriod === updateStatus.logicalPeriod
                        ) {
                            afr.status = updateStatus.status;
                        }
                    }
                }
            }
        },
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(loadEntity.pending, (state: any) => {
                state.status = "loadEntityData";
            })
            .addCase(loadEntity.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.entity = action.payload;
            })
            .addCase(loadEditEntity.pending, (state: any) => {
                state.status = "loadEntityData";
            })
            .addCase(loadEditEntity.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.entity = action.payload;
            })
            .addCase(saveEntityDetail.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(saveEntityDetail.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                if (action.payload) {
                    state.entity = action.payload;
                }
            })
            .addCase(saveEntityReportAccesses.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(saveEntityReportAccesses.fulfilled, (state: any, action: any) => {

                if (action.payload) {
                    state.entity.entityReportAccessChanges = action.payload;
                }
                state.status = "idle";
            })
            .addCase(getEntityHistory.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getEntityHistory.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
    },
});

export const { resetEntityState, updateAddress, updateAfrStatus } = entitySlice.actions;

export const getSelectedEntity = (state: RootState) => state.entity ? state.entity.entity : null;
export const selectedEntityAddress = (state: RootState) =>
    state.entity ?
        state.entity.entity ?
            state.entity.entity.address ? state.entity.entity.address : null
            : null
        : null;

export const selectAfrs = (state: RootState) =>
    state.entity ?
        state.entity.entity ?
            state.entity.entity.afrs ? state.entity.entity.afrs : []
            : []
        : [];

export const selectEntityReportAccessChangeEvents = (state: RootState) =>
    state.entity ?
        state.entity.entity ?
            state.entity.entity.entityReportAccessEvents ? state.entity.entity.entityReportAccessEvents
                : null : null : null;

export const selectedEntity = (state: RootState) => state.entity.entity;

export const selectEntityStateStatus = (state: RootState) => state.entity.status


export default entitySlice.reducer;